import { 
    Wind, 
    Mountain, 
    Droplets, 
    Layers, 
    Flame, 
    Calculator, 
    List 
} from 'lucide-react';

export const THEMES = {
    air:    { hex: '#22d3ee', tailwind: 'cyan-400',    bg: 'bg-cyan-400/10',    border: 'border-cyan-400/30',    hover: 'hover:bg-cyan-400/20' },
    earth:  { hex: '#10b981', tailwind: 'emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', hover: 'hover:bg-emerald-500/20' },
    water:  { hex: '#3b82f6', tailwind: 'blue-500',    bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    hover: 'hover:bg-blue-500/20' },
    fire:   { hex: '#f97316', tailwind: 'orange-500',  bg: 'bg-orange-500/10',  border: 'border-orange-500/30',  hover: 'hover:bg-orange-500/20' },
    slate:  { hex: '#94a3b8', tailwind: 'slate-400',   bg: 'bg-slate-800',      border: 'border-slate-700',      hover: 'hover:bg-slate-700' }
};

export const TABS = [
    { id: 'dashboard', label: 'Vision',  icon: Wind,       element: 'air' },
    { id: 'allocation',label: 'Assets',  icon: Mountain,   element: 'earth' },
    { id: 'income',    label: 'Inflow',  icon: Droplets,   element: 'water' },
    { id: 'waterfall', label: 'Flow',    icon: Layers,     element: 'water' },
    { id: 'expenses',  label: 'Burn',    icon: Flame,      element: 'fire' },
    { id: 'debt',      label: 'Debt',    icon: Calculator, element: 'fire' },
    { id: 'tracker',   label: 'Track',   icon: List,       element: 'fire' },
];

export const CHART_COLORS = { 
    earth: ['#10b981', '#34d399', '#059669'], 
    fire:  ['#f97316', '#fb923c', '#ea580c'] 
};

export const ASSET_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#f43f5e'];

export const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export const GOAL_COLORS = ['#f472b6', '#c084fc', '#22d3ee', '#fbbf24'];

export const LIABILITY_CATEGORIES = [
    "Mortgage",
    "Credit Card",
    "Student Loan",
    "Car Loan",
    "Personal Loan",
    "Medical Debt",
    "HELOC",
    "Tax Debt",
    "BNPL (Buy Now Pay Later)",
    "Other"
];

export const TOUR_STEPS = [
    {
        id: 'welcome',
        targetId: null, // Center screen
        title: 'Welcome to Four Elements Finance', 
        content: "Let's take a quick tour of your new financial architecture. We use the Four Elements to balance your wealth.",
        tab: 'dashboard'
    },
    {
        id: 'nav-air',
        targetId: 'nav-dashboard',
        title: 'Air (Vision)',
        content: "Start here. This is your cockpit. It projects your Net Worth trajectory and calculates your Financial Independence number.",
        tab: 'dashboard'
    },
    {
        id: 'master-tips-explainer',
        targetId: null, 
        title: 'Master Tips & Wisdom',
        content: "Every tab now starts with a collapsible 'Master Tip' card at the top. Click the header to expand it for deep insights into that specific Element.",
        tab: 'dashboard'
    },
    {
        id: 'nav-earth',
        targetId: 'nav-allocation',
        title: 'Earth (Assets)',
        content: "Earth is your foundation. Track your liquid cash, investments, and property here.",
        tab: 'allocation'
    },
    {
        id: 'action-add-asset',
        targetId: 'add-asset-btn', 
        title: 'First Mission',
        content: "Click here to add your first asset. Even a simple savings account is enough to start the projection engine.",
        tab: 'allocation'
    },
    {
        id: 'nav-water',
        targetId: 'nav-income',
        title: 'Water (Inflow)',
        content: "Water is flow. Track your salary and side hustles here. Then, use the 'Flow' tab to create waterfalls for your surplus cash.",
        tab: 'income'
    },
    {
        id: 'nav-fire',
        targetId: 'nav-expenses',
        title: 'Fire (Burn)',
        content: "Fire is consumption. Track fixed bills in 'Burn', liabilities in 'Debt', and daily spending in 'Track'. Keep the fire controlled.",
        tab: 'expenses'
    },
    {
        id: 'nav-profile',
        targetId: 'nav-user-menu',
        title: 'You Are Ready',
        content: "That's the loop. Check your Profile menu to view your Badges or replay this guide anytime.",
        tab: 'dashboard'
    }
];

export const DEFAULT_DATA = {
    // --- AIR (Vision) ---
    age: 30, 
    retireAge: 60, 
    lifeExpectancy: 90, 
    salary: 0, 
    salaryGrowth: 3.0, 
    inflation: 3.0,
    goals: [], 
    hasSeenIntro: false,
    hasSeenTour: false, 
    lastVisionReview: new Date().toISOString(), 
    scenario: { active: false, goals: [] },

    // --- WATER (Income & Flow) ---
    payFrequency: 26,
    deductions: [], 
    bonuses: [],    
    waterfall: [],  
    sinkingFunds: [], 

    // --- EARTH (Assets) ---
    assets: [], 

    // --- FIRE (Expenses, Debt, Tracker) ---
    expenseItems: [], 
    debts: [],        
    
    // Tracker Specifics
    trackerOffset: 0, // --- FIX: Persist Calendar Position ---
    trackerPeriodStart: new Date().toISOString().split('T')[0], 
    trackerPayFreq: 'bi-weekly', 
    discretionaryBudget: 0, 
    pendingBudgetAdjustment: 0,
    transactions: [],
    trackerHistory: [],
    paidBills: [],
    customCategories: ["Dining", "Shopping", "Transport", "Entertainment", "Groceries"],
    
    // Metadata
    lastModified: 0 // --- FIX: Add Timestamp for Sync Logic ---
};